import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import noop from 'lodash/noop';

import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import A from '@adsk/bim360-matrix-react-a';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import isAcc from './helpers/isAcc';

import exportSvg from '@adsk/bim360-matrix-react-assets/export.svg';

import './_ModulePicker.scss';

/**
 * @class ModulePicker
 **/
export class ModulePicker extends Component {
  /**
   * ModulePicker component constructor.
   * @param props
   */
  constructor(props) {
    super(props);

    const classicCategory = 'classic';
    const nextGenCategory = 'next_gen';
    const adminCategory = 'admin';

    this.isClassicModule = module => module.category === classicCategory;
    this.isPickerModule = module =>
      [adminCategory, nextGenCategory].includes(module.category) && module.icon && module.url;
    this.getModuleType = module => module.type;
    this.hasClassicModules = this.props.modules.filter(this.isClassicModule).length > 0;

    this.defaultLinks = {
      termsAndConditions: props[TSymbolProp].translate('TERMS_AND_CONDITIONS_LINK'),
      privacy: props[TSymbolProp].translate('PRIVACY_LINK')
    };
  }

  getOnClick(originalOnClick = () => {}) {
    return (...args) => {
      this.props.onActionClicked(...args);
      return originalOnClick(...args);
    };
  }

  moduleClicked(e, module, isModuleSelected) {
    e.preventDefault();

    if (!isModuleSelected && module.onClick) {
      module.onClick();
    }
  }

  /**
   *
   * @param icon
   * @returns {XML}
   */
  renderModuleIcon(icon) {
    const isExternalIcon = icon.indexOf('http') !== -1;
    return isExternalIcon ? (
      <img className="ModulePicker__module-icon" src={icon} />
    ) : (
      <SvgIcon className="ModulePicker__module-icon" svgId={icon} />
    );
  }

  getTarget(module) {
    if (module.target) {
      return module.target;
    }
    return isAcc(module.url) ? '_blank' : undefined;
  }

  getAAttributes(module, moduleSelected) {
    const aAttributes = {
      href: module.url,
      target: this.getTarget(module),
      rel: 'noreferrer noopener'
    };
    if (module.onClick) {
      aAttributes.onClick = this.getOnClick(e => this.moduleClicked(e, module, moduleSelected));
    }
    return aAttributes;
  }

  /**
   *
   * @param module
   * @returns {VDOM}
   */
  renderPickerModules() {
    const baseModuleClassName = 'ModulePicker__module';
    const baseContainerClassName = 'ModulePicker__module-container';
    const isSelected = module =>
      this.getModuleType(module) === this.getModuleType(this.props.selectedModule);
    const getClassName = (selected, base) => (selected ? `${base}--selected` : base);
    const pickerModules = this.props.modules.filter(this.isPickerModule);

    return pickerModules.map(module => {
      const { icon, name } = module;
      const moduleSelected = isSelected(module);
      const containerClassNames = getClassName(moduleSelected, baseContainerClassName);
      const moduleClassNames = getClassName(moduleSelected, baseModuleClassName);

      // If no shouldShow property is passed to the module, default to "true"
      if (module.shouldShow !== false) {
        const aAttributes = this.getAAttributes(module, moduleSelected);

        return (
          <div key={this.getModuleType(module)} className={containerClassNames}>
            {moduleSelected && (
              <img
                className="ModulePicker__selected-module-tick"
                src="data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+Cjxzdmcgd2lkdGg9IjE4cHgiIGhlaWdodD0iMThweCIgdmlld0JveD0iMCAwIDE4IDE4IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPCEtLSBHZW5lcmF0b3I6IHNrZXRjaHRvb2wgNDEgKDM1MzI2KSAtIGh0dHA6Ly93d3cuYm9oZW1pYW5jb2RpbmcuY29tL3NrZXRjaCAtLT4KICAgIDx0aXRsZT44NkRDRjM4Mi01MEEwLTRFRjItQjRENi0xMjVBQTZEQkU5NUU8L3RpdGxlPgogICAgPGRlc2M+Q3JlYXRlZCB3aXRoIHNrZXRjaHRvb2wuPC9kZXNjPgogICAgPGRlZnM+PC9kZWZzPgogICAgPGcgaWQ9IlBhZ2UtMSIgc3Ryb2tlPSJub25lIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9IndoaXRlIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxnIGlkPSJNZW51cyIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTY1NC4wMDAwMDAsIC04MTIuMDAwMDAwKSIgZmlsbD0iIzAwOUVFMiI+CiAgICAgICAgICAgIDxnIGlkPSJHcm91cC02IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSg2NTQuMDAwMDAwLCA4MTIuMDAwMDAwKSI+CiAgICAgICAgICAgICAgICA8cGF0aCBkPSJNOC43NDk4NjY2NywyLjI3MzczNjc1ZS0xMyBDMy45MTY1MzMzMywyLjI3MzczNjc1ZS0xMyAwLDMuOTE2NTMzMzMgMCw4Ljc0OTg2NjY3IEMwLDEzLjU4MzIgMy45MTY1MzMzMywxNy40OTk3MzMzIDguNzQ5ODY2NjcsMTcuNDk5NzMzMyBDMTMuNTgzMiwxNy40OTk3MzMzIDE3LjQ5OTczMzMsMTMuNTgzMiAxNy40OTk3MzMzLDguNzQ5ODY2NjcgQzE3LjQ5OTczMzMsMy45MTY1MzMzMyAxMy41ODMyLDIuMjczNzM2NzVlLTEzIDguNzQ5ODY2NjcsMi4yNzM3MzY3NWUtMTMgTDguNzQ5ODY2NjcsMi4yNzM3MzY3NWUtMTMgWiBNMTMuMDgzMiw3LjcwODI2NjY3IEw4LjY2NjY2NjY3LDExLjg3NDkzMzMgQzguMzMzMzMzMzMsMTIuMTY2NCA3LjgzMzA2NjY3LDEyLjE2NjQgNy40OTk3MzMzMywxMS44MzMwNjY3IEw1LjI0OTg2NjY3LDkuNTQxNiBDNC45MTY1MzMzMyw5LjIwODI2NjY3IDQuOTE2NTMzMzMsOC42NjY2NjY2NyA1LjI0OTg2NjY3LDguMzc0OTMzMzMgQzUuNTgzMiw4LjA4MzIgNi4xMjQ4LDguMDQxNiA2LjQxNjUzMzMzLDguMzc0OTMzMzMgTDguMDgzMiwxMC4wODMyIEwxMS45MTY1MzMzLDYuNDk5NzMzMzMgQzEyLjI5MTQ2NjcsNi4yNDk4NjY2NyAxMi43OTE0NjY3LDYuMzMzMzMzMzMgMTMuMDgzMiw2LjY2NjY2NjY3IEMxMy4zMzMzMzMzLDYuOTU4MTMzMzMgMTMuMzMzMzMzMyw3LjM3NDkzMzMzIDEzLjA4MzIsNy43MDgyNjY2NyBMMTMuMDgzMiw3LjcwODI2NjY3IFoiIGlkPSJQYWdlLTEiPjwvcGF0aD4KICAgICAgICAgICAgPC9nPgogICAgICAgIDwvZz4KICAgIDwvZz4KPC9zdmc+Cg=="
              />
            )}
            <A className={moduleClassNames} {...aAttributes}>
              {this.renderModuleIcon(icon)}
              <span className="ModulePicker__module-name">{name}</span>
            </A>
          </div>
        );
      }

      return null;
    });
  }

  renderClassicModules() {
    const classicModules = this.props.modules.filter(this.isClassicModule).map(module => {
      const { name, url } = module;
      return (
        <A
          key={this.getModuleType(module)}
          className="ModulePicker__classic-module-row"
          href={url}
          target="_blank"
          onClick={this.getOnClick()}
        >
          {name}
          <SvgIcon className="ModulePicker__classic-module-link-icon">{exportSvg}</SvgIcon>
        </A>
      );
    });

    return (
      <div>
        <div className="ModulePicker__classic-modules-container">{classicModules}</div>
        <div className="ModulePicker__separator" />
      </div>
    );
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    if (this.props.modules.some(module => !module.type)) {
      console.error(`You're either using an old version of entitlements api or you're not passing type for each module.
      Support for id attribute will be removed in a future version of the package.`);
    }

    const links = { ...this.defaultLinks, ...this.props.links };

    return (
      <div className="ModulePicker">
        <div className="ModulePicker__container">{this.renderPickerModules()}</div>
        <div className="ModulePicker__separator" />
        {this.hasClassicModules > 0 && this.renderClassicModules()}
        <footer className="ModulePicker__footer">
          <div className="ModulePicker__links">
            <A href={links.privacy} target="_blank" onClick={this.getOnClick()}>
              {this.props[TSymbolProp].translate('PRIVACY')}
            </A>
            <span className="ModulePicker__links-separator">|</span>
            <A href={links.termsAndConditions} target="_blank" onClick={this.getOnClick()}>
              {this.props[TSymbolProp].translate('TERMS_AND_CONDITIONS')}
            </A>
          </div>
          <div className="ModulePicker__copyright">
            {this.props[TSymbolProp].translate('COPYRIGHTS', { year: new Date().getFullYear() })}
          </div>
        </footer>
      </div>
    );
  }
}
ModulePicker.defaultProps = {
  onActionClicked: noop
};

ModulePicker.propTypes = {
  /**
   * The selected module that the module picker displays
   */
  selectedModule: PropTypes.shape({
    /**
     * The type of the module (used to match the selectedModule with) - should be used, this is a newer api.
     */
    type: PropTypes.string,
    /**
     * The name of the module
     */
    name: PropTypes.string.isRequired
  }),
  /**
   * The locale to use for this component
   */
  locale: PropTypes.string.isRequired,
  /**
   * Collection of links that are specific for the current module
   */
  links: PropTypes.shape({
    /**
     * The terms and conditions link for the current module
     */
    termsAndConditions: PropTypes.string,
    /**
     * The privacy link for the current module
     */
    privacy: PropTypes.string
  }),
  /**
   * All the available modules to show in the module picker
   */
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * A string name that will be displayed
       */
      name: PropTypes.string.isRequired,
      /**
       * The category of the module (determines whether the module is Next Gen/Admin or Classic)
       */
      category: PropTypes.string.isRequired,
      /**
       * The type of the module (used to match the selectedModule with) - should be used, this is a newer api.
       */
      type: PropTypes.string,
      /**
       * The url to navigate from that module
       */
      url: PropTypes.string,
      /**
       * Optional function to run when the module is clicked. URL property will not be used if this property is present
       */
      onClick: PropTypes.func,
      /**
       * The icon id to display for that module
       */
      icon: PropTypes.string,
      /**
       * Whether to display the module or not
       */
      shouldShow: PropTypes.bool,
      /**
       * The link target for clicking on the module. Defaults to `_self`
       */
      target: PropTypes.string
    })
  ),
  /**
   * Event handler that's called when any action is clicked inside the module picker.
   */
  onActionClicked: PropTypes.func
};

export default withLocale(
  ModulePicker,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
